import { UnstyledButton } from 'shared/components/Button/Button.styles';
import Text from 'shared/components/Text/Text';
import styled, { css } from 'styled-components';
import { DefaultTheme } from 'styled-components/dist/types';

export enum Status {
  COMPLETED = 'COMPLETED',
  IN_PROGRESS = 'IN PROGRESS',
  INCOMPLETE = 'INCOMPLETE',
  HIGHLIGHTED = 'HIGHLIGHTED',
}

export const IconSpacer = styled.div`
  min-width: 16px;
`;

export const StatusTrackerItemWrapper = styled.div<{
  $isSubtask: boolean;
  $margin?: string;
  $disabled: boolean;
}>`
  ${({ $isSubtask, $margin, $disabled, theme }) => `
    display: flex;
    height: 40px;
    margin: ${$margin || ($isSubtask ? '0 0 2px 0' : '0 0 36px 0')};
    cursor: pointer;
    ${$disabled && 'pointer-events: none;'}

    @media screen and (max-width: ${theme.breakpoints.small}) {
      margin: ${$margin || ($isSubtask ? '0 0 0 0' : '0 0 24px 0')};
    }
  `}
`;

type LineProps = {
  $showCompleted: boolean;
  $showLine: boolean;
  $isSubtask: boolean;
};

export const VerticalLine = styled.div<LineProps>`
  ${({ $showLine, $showCompleted, theme: { colors }, $isSubtask }) =>
    $showLine
      ? css`
          position: relative;
          left: ${$isSubtask ? '5px' : ''};
          transform: translate(50%, 0);
          height: calc(120%);
          z-index: 0;
          border-left: 1px solid
            ${$showCompleted
              ? colors.strokeInteractionActiveSecondary2100
              : colors.strokePrimaryGrey20};
        `
      : `display: none;`}
`;

const getIconBorder = (theme: DefaultTheme, status: Status, isSubtask: boolean) => {
  if (status !== Status.INCOMPLETE)
    return `1px solid ${theme.colors.strokeInteractionActiveSecondary2100}`;

  return isSubtask
    ? `1px solid ${theme.colors.strokePrimaryGrey20}`
    : `1px solid ${theme.colors.strokeSecondaryGrey10}`;
};

type StatusTrackerProps = {
  $status: Status;
  $isSubtask: boolean;
};

export const StatusTrackerIconContainer = styled.div<StatusTrackerProps>`
  ${({ theme, $status, $isSubtask }) => `
    display: flex;
    position: relative;
    transform: ${$isSubtask ? 'translate(0, 60%)' : ''};
    padding: ${$isSubtask ? '4px' : '12px'};
    align-items: center;
    border-radius: 8px;
    height: ${$isSubtask ? '16px' : '40px'};
    width: ${$isSubtask ? '16px' : '40px'};
    background: ${$isSubtask && $status === Status.COMPLETED ? theme.colors.buttonButtonPrimarySecondary2100 : theme.colors.cardElevationLowWhite};
    box-shadow: ${theme.elevations.mid};
    border: ${getIconBorder(theme, $status, $isSubtask)};
    margin: ${$isSubtask ? '0 0 0 12px' : '0'};
    z-index: 5;

    svg {
      height: 16px;
      width: 16px;
    }
  `}
`;

export const StatusTrackerItemDetails = styled(UnstyledButton)`
  display: flex;
  flex-direction: column;
  margin-left: 24px;
  width: 100%;
  align-self: center;
`;

export const StatusTrackerLabel = styled(Text)<StatusTrackerProps>`
  ${({ theme, $status, $isSubtask }) => `
      margin: 0;
      line-height: ${$isSubtask ? '1.42' : '1.5'};
      font-size: ${$status === Status.IN_PROGRESS && !$isSubtask ? '16px' : '14px'};
      font-weight: ${$status === Status.IN_PROGRESS ? '700' : '500'};
      color: ${$status === Status.INCOMPLETE ? theme.colors.textSecondarySubtleGrey80 : theme.colors.textPrimarySecondary1100};
    `}
`;

export const StatusTrackerSublabel = styled(Text)<Omit<StatusTrackerProps, '$status'>>`
  ${({ theme, $isSubtask }) => `
    font-weight: ${$isSubtask ? '700' : '400'};
    color: ${$isSubtask ? theme.colors.chartChartMagentaHoverAccent1120 : theme.colors.textSecondarySubtleGrey80};
  `}
`;

export const OnboardingNavWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    flex: 1;

    @media screen and (max-width: ${theme.breakpoints.small}) {
      margin-top: 16px;
    }
  `}
`;

export const AddressDisplayWrapper = styled.div`
  display: flex;
  gap: 12px;
  max-height: 24px;
  margin-bottom: 64px;

  p {
    cursor: default;
  }
`;
